/**
 * @fileoverview Core lifecycle state definitions for onboarding tours.
 *
 * This module provides lightweight, zero-dependency utilities for tracking
 * tour lifecycle states using the Performance API. Designed to be imported
 * across different packages without pulling in tour-specific dependencies.
 *
 * For higher-level utilities that depend on tour rendering logic, see:
 * `onboarding-tours/util/tourLifecycleUtils`
 *
 * @packageDocumentation
 */

/**
 * Defines the lifecycle states of an onboarding tour.
 * States progress in order: INIT → LIB_LOADED → CONFIG_LOADING → CONFIG_LOADED →
 * TOUR_STARTING → TOUR_ACTIVE → TOUR_ENDED
 * Special states: NO_TOUR (page has no tour), CONFIG_LOAD_ERROR, TOUR_LOAD_CANCELLED
 */
export const TourLifecycleState = {
  /** No tour available for current page (shouldRenderTour returned false) */
  NO_TOUR: 'no-tour',
  /** Initial state - library should check if tour needs to be rendered */
  INIT: 'tour-init',
  /** Tour library initialized and component mounted (shouldRenderTour returned true) */
  LIB_LOADED: 'tour-lib-loaded',
  /** Fetching tour configuration from API */
  CONFIG_LOADING: 'tour-config-loading',
  /** Tour configuration successfully loaded and transformed */
  CONFIG_LOADED: 'tour-config-loaded',
  /** Failed to load tour configuration */
  CONFIG_LOAD_ERROR: 'tour-config-load-failed',
  /** Tour loading was cancelled (component unmounted during load) */
  TOUR_LOAD_CANCELLED: 'tour-load-cancelled',
  /** Tour component rendered and about to start */
  TOUR_STARTING: 'tour-starting',
  /** Tour has started and is showing steps to the user */
  TOUR_ACTIVE: 'tour-active',
  /** Tour closed or completed by user */
  TOUR_ENDED: 'tour-ended'
};

/** Metadata attached to lifecycle state changes */

/** Performance mark with lifecycle state detail */

/** Performance mark name used for all lifecycle state transitions */
export const TOUR_LIFECYCLE_MARK_NAME = 'onboarding-tours-state-changed';

/**
 * Get the current tour lifecycle state from performance marks only.
 * Returns null if no marks exist (no fallback to URL or storage checks).
 *
 * This is a lightweight function that only reads from Performance API,
 * making it suitable for use in any package without dependencies.
 */
export function getTourLifecycleMarkMetadata() {
  const marks = performance.getEntriesByName(TOUR_LIFECYCLE_MARK_NAME, 'mark');
  const lastMark = marks[marks.length - 1];
  return lastMark ? lastMark.detail : null;
}